import React, { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Header } from "@/components/Header";
import { Profile } from "@/types/dashboard";
import { useToast } from "@/components/ui/use-toast";
import { Copy, CheckCircle2, Calendar, Users, BarChart3, Settings, LogOut, PencilIcon, Share2, Loader } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "@/hooks/useAnalytics";
import { LastActivityInfo } from "@/components/dashboard/settings/LastActivityInfo";
import { signOut } from "@/lib/auth";
import { useTranslation } from 'react-i18next';

const ProfilePage = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("User");
  const [serviceName, setServiceName] = useState("Services");
  const [serviceCount, setServiceCount] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  const [requestCount, setRequestCount] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { trackAction } = useAnalytics({ component: "ProfilePage" });
  const { t } = useTranslation();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError || !userData.user) {
        console.error('Error fetching user:', userError);
        setLoading(false);
        return;
      }
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) {
        console.error('Error fetching profile:', profileError);
        setLoading(false);
        return;
      }
      
      if (profileData) {
        setProfile(profileData);
        setDisplayName(`${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || 'User');
        
        try {
          const serviceType = profileData.service_type;
          
          if (serviceType === "Beauty & Wellness") {
            setServiceName("Beauty Services");
          } else if (serviceType === "Home Services") {
            setServiceName("Home Services");
          } else if (serviceType === "Professional Services") {
            setServiceName("Professional Services");
          } else if (serviceType === "Health & Fitness") {
            setServiceName("Health Services");
          } else if (serviceType === "Education & Tutoring") {
            setServiceName("Education Services");
          } else if (serviceType === "Tech Services") {
            setServiceName("Tech Services");
          } else {
            setServiceName("Services");
          }
          
          const { count: servicesCount, error: servicesError } = await supabase
            .from('services')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userData.user.id);
          
          if (servicesError) {
            console.error('Error fetching service count:', servicesError);
          } else {
            setServiceCount(servicesCount || 0);
          }
          
          const { count: customersCount, error: customersError } = await supabase
            .from('customers')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userData.user.id);
          
          if (customersError) {
            console.error('Error fetching customer count:', customersError);
          } else {
            setCustomerCount(customersCount || 0);
          }
          
          const { count: requestsCount, error: requestsError } = await supabase
            .from('service_requests')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userData.user.id);
          
          if (requestsError) {
            console.error('Error fetching request count:', requestsError);
          } else {
            setRequestCount(requestsCount || 0);
          }
        } catch (error) {
          console.error('Error fetching service count:', error);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      trackAction('sign_out');
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to sign out. Please try again.",
      });
    }
  };

  const copyServicePageLink = () => {
    const link = getServicePageLink();
    if (link) {
      navigator.clipboard.writeText(link);
      setLinkCopied(true);
      trackAction('copy_service_page_link');
      toast({
        title: "Link Copied",
        description: "Your service page link has been copied to clipboard.",
      });
      setTimeout(() => setLinkCopied(false), 3000);
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You need to set a service page link first.",
      });
    }
  };

  const getServicePageLink = (): string => {
    return profile?.service_page_link ? `${window.location.origin}/s/${profile.service_page_link}` : '';
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header />
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader className="h-8 w-8 text-gebeya-pink animate-spin" />
            </div>
          ) : profile ? (
            <>
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="flex-shrink-0">
                  <Avatar className="h-20 w-20 border-2 border-gebeya-pink">
                    <AvatarImage src={profile.profile_image_url || undefined} alt="Profile" />
                    <AvatarFallback className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white text-xl">
                      {profile.company_name?.charAt(0).toUpperCase() || 
                       profile.first_name?.charAt(0).toUpperCase() || 
                       profile.last_name?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </div>
                
                <div className="flex-grow">
                  <h1 className="text-2xl font-bold mb-2">{displayName}</h1>
                  <div className="text-gray-600 mb-2">
                    {profile.service_type || serviceName}
                  </div>
                  <LastActivityInfo lastUpdate={profile.last_update} />
                </div>
                
                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={() => navigate('/settings')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit Profile
                  </Button>
                  
                  <Button
                    onClick={copyServicePageLink}
                    className="flex items-center gap-2 bg-gebeya-pink hover:bg-gebeya-pink/90 text-white"
                  >
                    <Share2 className="h-4 w-4" />
                    {linkCopied ? "Link Copied!" : "Share My Page"}
                  </Button>
                </div>
              </div>
            
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <Card className="md:col-span-1">
                  <CardHeader className="flex flex-col items-center text-center">
                    {loading ? (
                      <Skeleton className="h-24 w-24 rounded-full" />
                    ) : (
                      <div className="flex flex-col gap-4">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Service Page Link</p>
                          <div className="flex items-center justify-center gap-2">
                            <Badge variant="outline" className="text-xs truncate max-w-[200px]">
                              {profile?.service_page_link ? `s/${profile.service_page_link}` : "Not set"}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={copyServicePageLink}
                              disabled={!profile?.service_page_link}
                            >
                              {linkCopied ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <Separator />
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div>
                            <p className="text-2xl font-bold">{serviceCount}</p>
                            <p className="text-xs text-gray-500">{serviceName}</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{customerCount}</p>
                            <p className="text-xs text-gray-500">{t('profile.customers')}</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{requestCount}</p>
                            <p className="text-xs text-gray-500">{t('profile.requests')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="text-center">
                    {loading ? (
                      <Skeleton className="h-10 w-full" />
                    ) : (
                      <div className="flex flex-col gap-4">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Service Page Link</p>
                          <div className="flex items-center justify-center gap-2">
                            <Badge variant="outline" className="text-xs truncate max-w-[200px]">
                              {profile?.service_page_link ? `s/${profile.service_page_link}` : "Not set"}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={copyServicePageLink}
                              disabled={!profile?.service_page_link}
                            >
                              {linkCopied ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <Separator />
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div>
                            <p className="text-2xl font-bold">{serviceCount}</p>
                            <p className="text-xs text-gray-500">{serviceName}</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{customerCount}</p>
                            <p className="text-xs text-gray-500">{t('profile.customers')}</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{requestCount}</p>
                            <p className="text-xs text-gray-500">{t('profile.requests')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                  <CardFooter className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate('/settings')}
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      {t('navigation.settings')}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={handleSignOut}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      {t('profile.signOut')}
                    </Button>
                  </CardFooter>
                </Card>

                <div className="md:col-span-2">
                  <Tabs defaultValue="overview">
                    <TabsList className="grid grid-cols-3 mb-6">
                      <TabsTrigger value="overview">{t('profile.overview')}</TabsTrigger>
                      <TabsTrigger value="calendar">{t('profile.calendar')}</TabsTrigger>
                      <TabsTrigger value="analytics">{t('profile.analytics')}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>{t('profile.quickActions')}</CardTitle>
                          <CardDescription>
                            {t('profile.quickActionsDesc')}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-4">
                          <Button
                            variant="outline"
                            className="h-24 flex flex-col gap-2"
                            onClick={() => navigate('/services')}
                          >
                            <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                              <span className="text-xl">🛠️</span>
                            </div>
                            <span>{t('profile.manageServices')}</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="h-24 flex flex-col gap-2"
                            onClick={() => navigate('/requests')}
                          >
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-xl">📋</span>
                            </div>
                            <span>{t('profile.viewRequests')}</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="h-24 flex flex-col gap-2"
                            onClick={() => navigate('/customers')}
                          >
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                              <Users className="h-5 w-5 text-green-600" />
                            </div>
                            <span>{t('profile.customers')}</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="h-24 flex flex-col gap-2"
                            onClick={() => navigate('/availability')}
                          >
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <Calendar className="h-5 w-5 text-purple-600" />
                            </div>
                            <span>{t('profile.availability')}</span>
                          </Button>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle>{t('profile.recentActivity')}</CardTitle>
                          <CardDescription>
                            {t('profile.recentActivityDesc')}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {loading ? (
                            <div className="space-y-4">
                              <Skeleton className="h-14 w-full" />
                              <Skeleton className="h-14 w-full" />
                              <Skeleton className="h-14 w-full" />
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <p>{t('profile.noRecentActivity')}</p>
                              <Button
                                variant="link"
                                className="mt-2"
                                onClick={() => navigate('/dashboard')}
                              >
                                {t('profile.goToDashboard')}
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="calendar">
                      <Card>
                        <CardHeader>
                          <CardTitle>{t('profile.calendar')}</CardTitle>
                          <CardDescription>
                            {t('profile.calendarDesc')}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="h-[400px] flex items-center justify-center">
                          <div className="text-center">
                            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">{t('profile.calendarViewComingSoon')}</p>
                            <Button
                              variant="link"
                              className="mt-2"
                              onClick={() => navigate('/availability')}
                            >
                              {t('profile.setYourAvailability')}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="analytics">
                      <Card>
                        <CardHeader>
                          <CardTitle>{t('profile.analytics')}</CardTitle>
                          <CardDescription>
                            {t('profile.analyticsDesc')}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="h-[400px] flex items-center justify-center">
                          <div className="text-center">
                            <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">{t('profile.analyticsComingSoon')}</p>
                            <Button
                              variant="link"
                              className="mt-2"
                              onClick={() => navigate('/dashboard')}
                            >
                              {t('profile.goToDashboard')}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>{t('profile.errorFetchingProfile')}</p>
              <Button
                variant="link"
                className="mt-2"
                onClick={() => navigate('/dashboard')}
              >
                {t('profile.goToDashboard')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
